import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useLang } from '@/context/LangContext';
import { Star, Loader } from 'lucide-react';
import { toast } from 'sonner';

function StarRow({ value, onChange, readOnly = false, size = 20 }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(i => (
        <button
          key={i}
          disabled={readOnly}
          onClick={() => onChange?.(i)}
          data-testid={!readOnly ? `star-${i}` : undefined}
          className={`${readOnly ? '' : 'hover:scale-110 transition'} ${i <= value ? 'text-gold' : 'text-steel'}`}
          style={{ cursor: readOnly ? 'default' : 'pointer' }}
        >
          <Star className="fill-current" style={{ width: size, height: size }} />
        </button>
      ))}
    </div>
  );
}

export default function ReviewsPanel({ playId, currentUserId }) {
  const { t } = useLang();
  const [data, setData] = useState({ reviews: [], avg: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [my, setMy] = useState({ rating: 0, comment: '' });
  const [saving, setSaving] = useState(false);
  const [canReview, setCanReview] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reviews/play/${playId}`);
      setData(r.data);
      const mine = (r.data.reviews || []).find(rv => rv.user_id === currentUserId);
      if (mine) {
        setMy({ rating: mine.rating, comment: mine.comment });
        setCanReview(true);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [playId]);

  const submit = async () => {
    if (my.rating < 1) { toast.error(t('choose_star')); return; }
    setSaving(true);
    try {
      await api.post(`/reviews/play/${playId}`, my);
      toast.success(t('review_sent'));
      setCanReview(true);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403) {
        toast.error(t('must_buy_review'));
      } else {
        toast.error(detail || t('error_generic'));
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-card-steel border border-gold/30 rounded-lg p-5 mt-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-gold">{t('reviews_title')}</div>
          <div className="flex items-center gap-2 mt-1">
            <StarRow value={Math.round(data.avg)} readOnly size={16} />
            <span className="text-sm text-gray-300">{data.avg.toFixed(1)} · {data.count} {data.count === 1 ? t('review_singular') : t('review_plural')}</span>
          </div>
        </div>
      </div>

      {/* My review form */}
      <div className="border-t border-steel pt-3 mb-3">
        <div className="text-xs uppercase tracking-widest text-gold font-mono mb-2">{t('your_review')}</div>
        <StarRow value={my.rating} onChange={(v) => setMy(m => ({ ...m, rating: v }))} />
        <textarea
          data-testid="review-comment"
          rows={2}
          maxLength={1000}
          value={my.comment}
          onChange={e => setMy(m => ({ ...m, comment: e.target.value }))}
          placeholder={t('review_placeholder')}
          className="mt-2 w-full bg-navy-deep border border-steel focus:border-gold rounded px-3 py-2 text-white text-sm outline-none resize-none"
        />
        <button data-testid="submit-review" onClick={submit} disabled={saving}
          className="mt-2 inline-flex items-center gap-1 px-4 py-2 rounded-md bg-gold text-navy font-h uppercase tracking-wider text-xs font-semibold hover:bg-gold-light disabled:opacity-50">
          {saving ? <Loader className="w-3 h-3 animate-spin" /> : <Star className="w-3 h-3" />}
          {canReview ? t('update_review') : t('publish_review')}
        </button>
        <p className="text-[10px] text-gray-500 mt-1">{t('only_buyers_review')}</p>
      </div>

      {/* Reviews list */}
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {loading && <div className="text-gray-400 text-sm">{t('loading')}</div>}
        {data.reviews.map(r => (
          <div key={r.id} className="border-t border-steel pt-2">
            <div className="flex items-center justify-between">
              <div className="text-sm text-white font-semibold">{r.user_name || t('coach')}</div>
              <StarRow value={r.rating} readOnly size={14} />
            </div>
            {r.comment && <p className="text-sm text-gray-300 mt-1">{r.comment}</p>}
            <div className="text-[10px] text-gray-500 mt-1">{r.created_at?.slice(0, 10)}</div>
          </div>
        ))}
        {!loading && !data.reviews.length && (
          <p className="text-xs text-gray-500 italic mt-2">{t('no_reviews_yet')}</p>
        )}
      </div>
    </div>
  );
}
